import React, { useState, useEffect } from "react";

// ─── Inline constants (so this file is self-contained for preview) ─────────
const FIELD_TYPES = {
  number:     { label: "Number",           hint: "Integer or decimal value" },
  currency:   { label: "Currency",         hint: "Formatted with your symbol e.g. ₦K / $M" },
  percentage: { label: "Percentage (0–1)", hint: "Enter 0.75 for 75%" },
  text:       { label: "Text / Notes",     hint: "Freeform text or notes" },
};

const CHART_TYPES = [
  { value: "bar",  label: "Bar Chart",  icon: "▦" },
  { value: "line", label: "Line Chart", icon: "↗" },
  { value: "area", label: "Area Chart", icon: "◿" },
];

const GRADIENTS = [
  { key: "blue",   label: "Ocean Blue",   css: "linear-gradient(135deg,#0052CC,#0041B8)", text: "#fff" },
  { key: "green",  label: "Forest Green", css: "linear-gradient(135deg,#107C10,#0B5C0B)", text: "#fff" },
  { key: "orange", label: "Sunset",       css: "linear-gradient(135deg,#D83B01,#A52801)", text: "#fff" },
  { key: "purple", label: "Violet",       css: "linear-gradient(135deg,#5C2D91,#40208C)", text: "#fff" },
  { key: "teal",   label: "Teal",         css: "linear-gradient(135deg,#008272,#005F53)", text: "#fff" },
  { key: "gold",   label: "Gold",         css: "linear-gradient(135deg,#986F0B,#7A5800)", text: "#fff" },
];

const SAMPLE_CATEGORIES = [
  "Contracts & Procurement", "Purchase Orders",
  "Diesel Inventory", "Logistics", "Projects", "Notes",
];

const EMPTY = {
  name: "", key: "", type: "number", category: "", newCategory: "",
  unit: "", icon: "📊", cardGradient: "blue",
  showOnDashboard: true, showInChart: true, chartType: "bar",
};

function toKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Label({ children, required }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
      {children}
      {required && <span style={{ color: "#EF4444", marginLeft: 3 }}>*</span>}
    </label>
  );
}

function StyledInput({ error, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      onFocus={e => { setFocused(true); props.onFocus?.(e); }}
      onBlur={e => { setFocused(false); props.onBlur?.(e); }}
      style={{
        width: "100%", padding: "10px 14px", fontSize: 14, fontFamily: "inherit",
        background: "#fff", color: "#111827",
        border: `1.5px solid ${error ? "#EF4444" : focused ? "#0052CC" : "#D1D5DB"}`,
        borderRadius: 10, outline: "none", transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focused ? "0 0 0 3px rgba(0,82,204,0.12)" : "none",
        boxSizing: "border-box",
        ...props.style,
      }}
    />
  );
}

function StyledSelect({ error, children, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <select
        {...props}
        onFocus={e => { setFocused(true); props.onFocus?.(e); }}
        onBlur={e => { setFocused(false); props.onBlur?.(e); }}
        style={{
          width: "100%", padding: "10px 38px 10px 14px", fontSize: 14, fontFamily: "inherit",
          background: "#fff", color: "#111827",
          border: `1.5px solid ${error ? "#EF4444" : focused ? "#0052CC" : "#D1D5DB"}`,
          borderRadius: 10, outline: "none", appearance: "none", cursor: "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: focused ? "0 0 0 3px rgba(0,82,204,0.12)" : "none",
          boxSizing: "border-box",
          ...props.style,
        }}
      >
        {children}
      </select>
      <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6B7280", fontSize: 12 }}>▼</div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 42, height: 24, borderRadius: 12, position: "relative", transition: "background 0.2s", flexShrink: 0,
          background: checked ? "#0052CC" : "#D1D5DB",
          boxShadow: checked ? "0 0 0 2px rgba(0,82,204,0.25)" : "none",
        }}
      >
        <div style={{
          position: "absolute", top: 3, left: checked ? 21 : 3, width: 18, height: 18,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }} />
      </div>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{label}</span>
    </label>
  );
}

function FieldGroup({ label, required, error, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label required={required}>{label}</Label>
      {children}
      {hint && !error && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9CA3AF" }}>{hint}</p>}
      {error && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#EF4444", fontWeight: 500 }}>⚠ {error}</p>}
    </div>
  );
}

// ─── Live Preview Card ─────────────────────────────────────────────────────
function PreviewCard({ form }) {
  const g = GRADIENTS.find(g => g.key === form.cardGradient) || GRADIENTS[0];
  const typeInfo = FIELD_TYPES[form.type];

  return (
    <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: g.css, padding: "22px 20px", color: "#fff", minHeight: 130 }}>
      <div style={{ position: "absolute", right: -20, top: -20, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
      <div style={{ position: "absolute", right: 10, bottom: -30, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.8 }}>
          {form.category || "Category"}
        </span>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          {form.icon || "📊"}
        </div>
      </div>

      {/* CHANGE 1: currency preview uses form.unit as symbol, falls back to ₦ */}
      <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 4, lineHeight: 1 }}>
        {form.type === "currency"
          ? `${form.unit || "₦"}0.0K`
          : form.type === "percentage"
          ? "0%"
          : form.unit ? `0 ${form.unit}` : "0"}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>{form.name || "Entity Name"}</div>

      <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {form.showOnDashboard && (
          <span style={{ fontSize: 10, background: "rgba(255,255,255,0.2)", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>Dashboard</span>
        )}
        {form.showInChart && (
          <span style={{ fontSize: 10, background: "rgba(255,255,255,0.2)", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
            {CHART_TYPES.find(c => c.value === form.chartType)?.icon} Chart
          </span>
        )}
        <span style={{ fontSize: 10, background: "rgba(255,255,255,0.15)", padding: "2px 8px", borderRadius: 20 }}>
          {typeInfo?.label}
        </span>
      </div>
    </div>
  );
}

// ─── Main Dialog ───────────────────────────────────────────────────────────
export default function EntityFormDialog({ open, onClose, onSave, editEntity, existingCategories = SAMPLE_CATEGORIES }) {
  const [form, setForm] = useState(EMPTY);
  const [useNewCat, setUseNewCat] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (editEntity) {
        setForm({ ...EMPTY, ...editEntity, newCategory: "" });
        setUseNewCat(!existingCategories.includes(editEntity.category));
      } else {
        setForm(EMPTY);
        setUseNewCat(false);
      }
      setErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEntity, open]);

  const set = (key) => (e) => {
    const val = e?.target?.value ?? e;
    setForm(f => {
      const next = { ...f, [key]: val };
      if (key === "name" && !editEntity) next.key = toKey(val);
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!editEntity) {
      // Key is disabled when editing — skip format check to avoid false errors on camelCase keys
      if (!form.key.trim()) errs.key = "Field key is required";
      else if (!/^[a-z_][a-z0-9_]*$/.test(form.key)) errs.key = "Lowercase letters, numbers and underscores only";
    }
    const cat = useNewCat ? form.newCategory : form.category;
    if (!cat?.trim()) errs.category = "Category is required";
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const category = useNewCat ? form.newCategory.trim() : form.category;
    onSave?.({ ...form, category, key: form.key.trim() });
  };

  const allCategories = [...new Set([...existingCategories, ...SAMPLE_CATEGORIES])];

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,15,40,0.55)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, animation: "fadeIn 0.15s ease",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        .entity-modal-scroll::-webkit-scrollbar { width: 6px; }
        .entity-modal-scroll::-webkit-scrollbar-track { background: #F3F4F6; border-radius: 3px; }
        .entity-modal-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
        .color-swatch:hover { transform: scale(1.15); }
        @media (max-width: 700px) {
          .modal-grid { grid-template-columns: 1fr !important; }
          .modal-preview { display: none !important; }
          .modal-preview-mobile { display: block !important; }
        }
      `}</style>

      <div style={{
        background: "#F9FAFB", borderRadius: 20,
        width: "100%", maxWidth: 880,
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,15,40,0.35), 0 0 0 1px rgba(255,255,255,0.1)",
        animation: "slideUp 0.2s ease",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "22px 28px 18px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: "linear-gradient(135deg,#001A3D,#0052CC)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, flexShrink: 0,
            }}>
              {editEntity ? "✏️" : "✨"}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>
                {editEntity ? "Edit Entity" : "Add New Entity"}
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                {editEntity ? "Update display and chart settings" : "New metrics appear everywhere instantly"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E5E7EB",
              background: "#fff", cursor: "pointer", fontSize: 18, color: "#6B7280",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s", flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.borderColor = "#FCA5A5"; e.currentTarget.style.color = "#EF4444"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#6B7280"; }}
          >
            ×
          </button>
        </div>

        {/* ── Body ── */}
        <div className="entity-modal-scroll" style={{ overflowY: "auto", flex: 1 }}>
          <div className="modal-grid" style={{ display: "grid", gridTemplateColumns: "1fr 300px" }}>

            {/* ── Left: Form ── */}
            <div style={{ padding: "24px 28px", borderRight: "1px solid #E5E7EB" }}>

              {/* Section: Identity */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 18, borderRadius: 2, background: "#0052CC" }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Identity</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 12 }}>
                  <FieldGroup label="Entity Name" required error={errors.name}>
                    <StyledInput value={form.name} onChange={set("name")} placeholder="e.g. Safety Incidents" error={errors.name} />
                  </FieldGroup>
                  <FieldGroup label="Icon">
                    <StyledInput
                      value={form.icon} onChange={set("icon")} maxLength={4} error={false}
                      style={{ fontSize: 22, textAlign: "center", padding: "8px 4px" }}
                    />
                  </FieldGroup>
                </div>

                <FieldGroup label="Field Key" required error={errors.key} hint="Auto-generated · used as storage identifier">
                  <StyledInput
                    value={form.key} onChange={set("key")}
                    disabled={!!editEntity} error={errors.key}
                    placeholder="e.g. safety_incidents"
                    style={{ fontFamily: "ui-monospace,monospace", fontSize: 13, background: editEntity ? "#F3F4F6" : "#fff", color: editEntity ? "#6B7280" : "#111827" }}
                  />
                </FieldGroup>
              </div>

              {/* Section: Category */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 18, borderRadius: 2, background: "#7C3AED" }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Category</span>
                  <div style={{ marginLeft: "auto" }}>
                    <Toggle checked={useNewCat} onChange={setUseNewCat} label="Create new" />
                  </div>
                </div>

                <FieldGroup label={useNewCat ? "New Category Name" : "Select Category"} required error={errors.category}>
                  {useNewCat ? (
                    <StyledInput value={form.newCategory} onChange={set("newCategory")} placeholder="e.g. Safety & Compliance" error={errors.category} />
                  ) : (
                    <StyledSelect value={form.category} onChange={set("category")} error={errors.category}>
                      <option value="" style={{ color: "#9CA3AF" }}>— Pick a category —</option>
                      {allCategories.map(c => <option key={c} value={c} style={{ color: "#111827" }}>{c}</option>)}
                    </StyledSelect>
                  )}
                </FieldGroup>
              </div>

              {/* Section: Data Type */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 18, borderRadius: 2, background: "#059669" }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Data</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FieldGroup label="Data Type" required>
                    <StyledSelect value={form.type} onChange={set("type")}>
                      {Object.entries(FIELD_TYPES).map(([k, v]) => (
                        <option key={k} value={k} style={{ color: "#111827" }}>{v.label}</option>
                      ))}
                    </StyledSelect>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9CA3AF" }}>{FIELD_TYPES[form.type]?.hint}</p>
                  </FieldGroup>

                  {/* CHANGE 2: currency removed from disabled list + dynamic hint & placeholder */}
                  <FieldGroup
                    label="Unit"
                    hint={form.type === "currency" ? "Currency symbol — e.g. ₦, $, €, GBP" : "Optional — e.g. L, days, trips"}
                  >
                    <StyledInput
                      value={form.unit}
                      onChange={set("unit")}
                      placeholder={form.type === "currency" ? "₦, $, €, GBP..." : "L, days, trips..."}
                      disabled={["text", "percentage"].includes(form.type)}
                      style={{ background: ["text", "percentage"].includes(form.type) ? "#F3F4F6" : "#fff" }}
                    />
                  </FieldGroup>
                </div>
              </div>

              {/* Section: Display */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 18, borderRadius: 2, background: "#D97706" }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Display</span>
                </div>

                <FieldGroup label="Card Colour">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {GRADIENTS.map(g => (
                      <div
                        key={g.key}
                        className="color-swatch"
                        onClick={() => setForm(f => ({ ...f, cardGradient: g.key }))}
                        title={g.label}
                        style={{
                          width: 36, height: 36, borderRadius: 10, background: g.css,
                          cursor: "pointer", flexShrink: 0, transition: "transform 0.15s",
                          outline: form.cardGradient === g.key ? "3px solid #111827" : "2px solid transparent",
                          outlineOffset: 2,
                          boxShadow: form.cardGradient === g.key ? "0 0 0 5px rgba(0,0,0,0.1)" : "0 2px 6px rgba(0,0,0,0.15)",
                        }}
                      />
                    ))}
                    <div style={{ display: "flex", alignItems: "center", marginLeft: 4 }}>
                      <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 500 }}>
                        {GRADIENTS.find(g => g.key === form.cardGradient)?.label}
                      </span>
                    </div>
                  </div>
                </FieldGroup>

                <FieldGroup label="Chart Type">
                  <div style={{ display: "flex", gap: 8 }}>
                    {CHART_TYPES.map(ct => (
                      <button
                        key={ct.value}
                        onClick={() => form.type !== "text" && setForm(f => ({ ...f, chartType: ct.value }))}
                        disabled={form.type === "text"}
                        style={{
                          flex: 1, padding: "10px 8px", borderRadius: 10, cursor: form.type === "text" ? "not-allowed" : "pointer",
                          border: "1.5px solid",
                          borderColor: form.chartType === ct.value ? "#0052CC" : "#E5E7EB",
                          background: form.chartType === ct.value ? "#EBF3FF" : "#fff",
                          color: form.chartType === ct.value ? "#0052CC" : "#6B7280",
                          fontWeight: form.chartType === ct.value ? 700 : 500,
                          fontSize: 12, transition: "all 0.15s",
                          opacity: form.type === "text" ? 0.4 : 1,
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                          fontFamily: "inherit",
                        }}
                      >
                        <span style={{ fontSize: 18 }}>{ct.icon}</span>
                        {ct.label}
                      </button>
                    ))}
                  </div>
                </FieldGroup>

                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "14px 16px", background: "#F3F4F6", borderRadius: 12 }}>
                  <Toggle
                    checked={form.showOnDashboard && form.type !== "text"}
                    onChange={v => form.type !== "text" && setForm(f => ({ ...f, showOnDashboard: v }))}
                    label="Show as card on Dashboard"
                  />
                  <div style={{ height: 1, background: "#E5E7EB" }} />
                  <Toggle
                    checked={form.showInChart && form.type !== "text"}
                    onChange={v => form.type !== "text" && setForm(f => ({ ...f, showInChart: v }))}
                    label="Show in Analytics Charts"
                  />
                  {form.type === "text" && (
                    <p style={{ margin: 0, fontSize: 11, color: "#9CA3AF", fontStyle: "italic" }}>Dashboard & chart options are disabled for text fields</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Right: Preview ── */}
            <div className="modal-preview" style={{ padding: "24px 20px", background: "#fff" }}>
              <Label>Live Preview</Label>
              <PreviewCard form={form} />

              <div style={{ marginTop: 20, padding: "14px", background: "#F9FAFB", borderRadius: 12, border: "1px solid #E5E7EB" }}>
                <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>Summary</p>
                {[
                  { label: "Name",     value: form.name || "—" },
                  { label: "Key",      value: form.key || "—", mono: true },
                  { label: "Category", value: (useNewCat ? form.newCategory : form.category) || "—" },
                  { label: "Type",     value: FIELD_TYPES[form.type]?.label || "—" },
                  { label: "Unit",     value: form.unit || "none" },
                ].map(({ label, value, mono }) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #F3F4F6" }}>
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>{label}</span>
                    <span style={{ fontSize: 11, color: "#111827", fontWeight: 600, fontFamily: mono ? "ui-monospace,monospace" : "inherit", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                {form.showOnDashboard && form.type !== "text" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#059669" }}>
                    <span>✓</span><span>Visible on Dashboard</span>
                  </div>
                )}
                {form.showInChart && form.type !== "text" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#0052CC" }}>
                    <span>✓</span><span>Visible in Analytics</span>
                  </div>
                )}
                {form.type === "text" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#7C3AED" }}>
                    <span>✓</span><span>Visible in Notes section</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "16px 28px", borderTop: "1px solid #E5E7EB", background: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0, gap: 12, flexWrap: "wrap",
        }}>
          <p style={{ margin: 0, fontSize: 12, color: "#9CA3AF" }}>
            {editEntity ? "Changes apply immediately across all views" : "Entity will appear in all views after saving"}
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px", borderRadius: 10, border: "1.5px solid #E5E7EB",
                background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600,
                cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#D1D5DB"; e.currentTarget.style.background = "#F9FAFB"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "#fff"; }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: "10px 24px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg,#0052CC,#0041B8)",
                color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
                transition: "all 0.15s", fontFamily: "inherit",
                boxShadow: "0 4px 12px rgba(0,82,204,0.35)",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,82,204,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,82,204,0.35)"; }}
            >
              {editEntity ? "Save Changes" : "Add Entity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Demo wrapper ──────────────────────────────────────────────────────────
export function Demo() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [saved, setSaved] = useState(null);

  const sampleEdit = {
    name: "Safety Incidents", key: "safety_incidents", type: "number",
    category: "Contracts & Procurement", icon: "⚠️", cardGradient: "orange",
    showOnDashboard: true, showInChart: true, chartType: "bar", unit: "cases",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#001A3D,#002D7A)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ color: "#fff", marginBottom: 32 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚙️</div>
          <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 700 }}>Entity Form Dialog</h1>
          <p style={{ margin: 0, opacity: 0.7, fontSize: 14 }}>Redesigned — responsive, visible dropdowns, live preview</p>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => { setMode("add"); setOpen(true); }}
            style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: "#0052CC", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 16px rgba(0,82,204,0.4)" }}>
            + Add New Entity
          </button>
          <button onClick={() => { setMode("edit"); setOpen(true); }}
            style={{ padding: "12px 28px", borderRadius: 12, border: "2px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            ✏ Edit Entity
          </button>
        </div>
        {saved && (
          <div style={{ marginTop: 24, background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: "14px 20px", color: "#fff", fontSize: 13, maxWidth: 360, margin: "24px auto 0" }}>
            <strong>✓ Saved:</strong> {saved.name} ({saved.key}) · {saved.category}
          </div>
        )}
      </div>
      <EntityFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={data => { setSaved(data); setOpen(false); }}
        editEntity={mode === "edit" ? sampleEdit : null}
        existingCategories={SAMPLE_CATEGORIES}
      />
    </div>
  );
}